import { useState, useEffect } from "react";
import { Send, RefreshCw } from "lucide-react";
import api from "../utils/api";
import { useToast } from "../components/Toast";
import { fmtDateTime } from "../utils/helpers";
import Card from "../components/Card";
import StatusBadge from "../components/StatusBadge";
import type { ManualSendRecord } from "../types";

export default function ManualSendPage() {
  const { toast } = useToast();
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sends, setSends] = useState<ManualSendRecord[]>([]);

  const fetchSends = async () => {
    try {
      const res = await api.get("/wa/send/recent");
      if (res.data.ok) setSends(res.data.sends || []);
    } catch {}
  };

  useEffect(() => { fetchSends(); }, []);

  const handleSend = async () => {
    if (!phone.trim() || !message.trim()) { toast("Phone and message required", "error"); return; }
    setSending(true);
    try {
      const res = await api.post("/wa/send", { phone: phone.trim(), message: message.trim() });
      if (res.data.ok) {
        toast("Queued send #" + res.data.send.id, "success");
        setPhone(""); setMessage("");
        fetchSends();
      } else toast(res.data.error || "Error", "error");
    } catch (err: any) {
      toast(err.response?.data?.error || "Error", "error");
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Manual Send</h1>
        <p className="text-zinc-400 text-sm mt-1">Send a WhatsApp message to any number</p>
      </div>

      <Card title="Compose Message">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">Phone number</label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+393331234567"
              className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-fuchsia-500 transition-colors"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm text-zinc-400 mb-1 block">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Ciao! ..."
              rows={2}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white placeholder-zinc-600 focus:outline-none focus:border-fuchsia-500 transition-colors resize-none"
            />
          </div>
          <div>
            <button
              onClick={handleSend}
              disabled={sending || !phone.trim() || !message.trim()}
              className="w-full px-4 py-2.5 rounded-xl bg-gradient-to-r from-fuchsia-600 to-orange-500 text-white font-semibold hover:from-fuchsia-500 hover:to-orange-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            >
              <Send size={16} />
              {sending ? "Sending..." : "Send"}
            </button>
          </div>
        </div>
      </Card>

      <Card title="Recent Sends" actions={
        <button onClick={fetchSends} className="text-zinc-400 hover:text-white transition-colors">
          <RefreshCw size={16} />
        </button>
      }>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-zinc-500 text-left border-b border-zinc-800">
                <th className="pb-3 font-medium">#</th>
                <th className="pb-3 font-medium">Phone</th>
                <th className="pb-3 font-medium">Message</th>
                <th className="pb-3 font-medium">Status</th>
                <th className="pb-3 font-medium">Created</th>
                <th className="pb-3 font-medium">Error</th>
              </tr>
            </thead>
            <tbody>
              {sends.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-zinc-600 py-8">No recent sends</td></tr>
              ) : sends.map((s) => (
                <tr key={s.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                  <td className="py-2.5">{s.id}</td>
                  <td className="py-2.5 font-mono text-xs">{s.phone}</td>
                  <td className="py-2.5 max-w-[200px] truncate">{s.message}</td>
                  <td className="py-2.5"><StatusBadge status={s.status} /></td>
                  <td className="py-2.5 text-xs text-zinc-500">{fmtDateTime(s.created_at)}</td>
                  <td className="py-2.5 text-xs text-red-400">{s.last_error || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
